/* ==========================================================================
   macOS Tahoe Design System — behaviors (vanilla JS, no dependencies)
   Theme/appearance, icons, toggles, segmented, tabs, sliders, popups & menus,
   context menus, popovers, tooltips, windows (drag/focus/traffic lights),
   dock magnification, Spotlight, Control Center, notifications, sheets.
   ========================================================================== */
(function (global) {
  'use strict';
  const doc = document;
  const $ = (s, r = doc) => r.querySelector(s);
  const $$ = (s, r = doc) => Array.from(r.querySelectorAll(s));
  const on = (el, ev, fn, opt) => el && el.addEventListener(ev, fn, opt);
  const emit = (el, name, detail) => el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const resolve = (ref, from) => {
    if (!ref) return null;
    if (ref instanceof Element) return ref;
    if (ref.startsWith('closest:')) return from ? from.closest(ref.slice(8)) : null;
    if (ref.startsWith('#') || ref.startsWith('.')) return $(ref);
    if (from) { const c = from.closest(ref); if (c) return c; }
    return doc.getElementById(ref) || $(ref);
  };

  /* ---------------- Theme / Appearance ---------------- */
  const Theme = {
    KEY: 'macos-tahoe-ds-prefs',
    root: doc.documentElement,
    media: global.matchMedia ? global.matchMedia('(prefers-color-scheme: dark)') : null,
    defaults: { theme: 'light', accent: 'blue', iconStyle: 'default', tint: true, menubarBg: false, reduceTransparency: false },
    prefs: {},
    load() {
      let p = {};
      try { p = JSON.parse(localStorage.getItem(this.KEY) || '{}'); } catch (e) { p = {}; }
      this.prefs = Object.assign({}, this.defaults, p);
      // URL overrides (not persisted): ?theme=dark&accent=purple&iconStyle=clear&tint=false
      try {
        const q = new URLSearchParams(location.search); let override = false;
        ['theme', 'accent', 'iconStyle'].forEach(k => { if (q.has(k)) { this.prefs[k] = q.get(k); override = true; } });
        ['tint', 'menubarBg', 'reduceTransparency'].forEach(k => { if (q.has(k)) { this.prefs[k] = q.get(k) !== 'false'; override = true; } });
        this._noSave = override;
      } catch (e) { /* ignore */ }
      this.render();
      if (this.media) this.media.addEventListener('change', () => { if (this.prefs.theme === 'auto') this.render(); });
    },
    set(key, value) { this.prefs[key] = value; this.render(); this.save(); },
    setMany(obj) { Object.assign(this.prefs, obj); this.render(); this.save(); },
    get(key) { return this.prefs[key]; },
    save() { if (this._noSave) return; try { localStorage.setItem(this.KEY, JSON.stringify(this.prefs)); } catch (e) { /* private mode */ } },
    resolvedTheme() { const t = this.prefs.theme; return t === 'auto' ? (this.media && this.media.matches ? 'dark' : 'light') : t; },
    render() {
      const r = this.root, p = this.prefs;
      r.dataset.theme = this.resolvedTheme();
      r.dataset.themeMode = p.theme;
      r.dataset.accent = p.accent;
      r.dataset.iconStyle = p.iconStyle;
      r.dataset.wallpaperTint = String(!!p.tint);
      r.dataset.menubarBg = String(!!p.menubarBg);
      r.dataset.reduceTransparency = String(!!p.reduceTransparency);
      syncThemeUI();
      emit(doc, 'mac:theme', Object.assign({}, p, { resolvedTheme: r.dataset.theme }));
    }
  };
  // Declarative bindings:
  //   data-pref="accent" data-value="purple"      → click sets pref; gets .is-selected/.is-active when current
  //   data-pref-toggle="tint"                      → click toggles boolean pref; aria-checked synced
  function syncThemeUI() {
    $$('[data-pref]').forEach(el => {
      const k = el.dataset.pref, v = el.dataset.value;
      const active = String(Theme.prefs[k]) === v;
      el.classList.toggle('is-selected', active);
      el.classList.toggle('is-active', active);
      if (el.matches('.mac-radio, .mac-checkbox')) el.checked = active;
      if (el.matches('.mac-popup, .mac-pulldown')) { /* label handled by data-pref-label */ }
    });
    $$('[data-pref-toggle]').forEach(el => {
      const v = !!Theme.prefs[el.dataset.prefToggle];
      if (el.classList.contains('mac-toggle')) el.setAttribute('aria-checked', String(v));
      else if ('checked' in el) el.checked = v;
      else el.classList.toggle('is-selected', v);
    });
    $$('[data-pref-label]').forEach(el => {
      const k = el.dataset.prefLabel; const v = Theme.prefs[k];
      const map = el.dataset.labels ? JSON.parse(el.dataset.labels) : null;
      el.textContent = map && map[v] != null ? map[v] : String(v).charAt(0).toUpperCase() + String(v).slice(1);
    });
  }
  on(doc, 'click', e => {
    const a = e.target.closest('[data-pref]');
    if (a) { Theme.set(a.dataset.pref, a.dataset.value); }
    const t = e.target.closest('[data-pref-toggle]');
    if (t) { const k = t.dataset.prefToggle; Theme.set(k, !Theme.prefs[k]); }
  });

  /* ---------------- Toggle switch ---------------- */
  on(doc, 'click', e => {
    const t = e.target.closest('.mac-toggle');
    if (!t || t.hasAttribute('disabled') || t.getAttribute('aria-disabled') === 'true') return;
    if (t.hasAttribute('data-pref-toggle')) return; // handled by pref binding (state re-rendered)
    const v = t.getAttribute('aria-checked') !== 'true';
    t.setAttribute('aria-checked', String(v));
    emit(t, 'change', { checked: v });
  });
  $$('.mac-toggle').forEach(t => { if (!t.hasAttribute('role')) t.setAttribute('role', 'switch'); if (!t.hasAttribute('aria-checked')) t.setAttribute('aria-checked', 'false'); });

  /* ---------------- Segmented control & tabs ---------------- */
  on(doc, 'click', e => {
    const b = e.target.closest('.mac-segmented > button');
    if (!b) return;
    const seg = b.parentElement;
    if (seg.dataset.pref) return; // pref-bound segmented: children carry data-pref; handled above
    $$(':scope > button', seg).forEach(x => x.classList.toggle('is-active', x === b));
    const val = b.dataset.value || b.textContent.trim();
    emit(seg, 'change', { value: val, button: b });
    if (b.dataset.tab) {
      const scope = resolve(seg.dataset.tabs, seg) || doc;
      $$('[data-panel]', scope).forEach(p => { if (p.closest('[data-tabs-root]') && p.closest('[data-tabs-root]') !== scope) return; p.hidden = p.dataset.panel !== b.dataset.tab; });
    }
  });

  /* ---------------- Sliders ---------------- */
  function syncSlider(s) {
    const min = +s.min || 0, max = +s.max || 100, v = +s.value;
    s.style.setProperty('--val', ((v - min) / (max - min) * 100) + '%');
  }
  function bindSliders(root) { $$('input.mac-slider', root || doc).forEach(s => { syncSlider(s); if (!s._mac) { s._mac = 1; on(s, 'input', () => syncSlider(s)); } }); }

  /* ---------------- Generic dismissable layers ---------------- */
  const Layers = { open: new Set() };
  function closeAll(except) {
    Layers.open.forEach(l => { if (l !== except) closeLayer(l); });
  }
  function closeLayer(l) {
    if (!l) return;
    Layers.open.delete(l);
    if (l._temp) l.remove(); else l.hidden = true;
    if (l._anchor) l._anchor.classList.remove('is-open', 'is-active-menu');
    if (l._onClose) l._onClose();
  }
  on(doc, 'pointerdown', e => {
    Layers.open.forEach(l => {
      if (l.contains(e.target)) return;
      if (l._anchor && l._anchor.contains(e.target)) return;
      if (l._keep && e.target.closest(l._keep)) return;
      closeLayer(l);
    });
  }, true);
  on(doc, 'keydown', e => { if (e.key === 'Escape') { closeAll(); Spotlight.close(); } });

  /* ---------------- Menus ---------------- */
  function positionBelow(anchor, el, opts = {}) {
    const container = el.offsetParent || doc.body;
    const cr = container === doc.body ? { left: 0, top: 0, width: innerWidth, height: innerHeight } : container.getBoundingClientRect();
    const ar = anchor.getBoundingClientRect();
    const gap = opts.gap != null ? opts.gap : 4;
    let left = ar.left - cr.left + (container === doc.body ? scrollX : 0);
    let top = ar.bottom - cr.top + gap + (container === doc.body ? scrollY : 0);
    if (opts.align === 'right') left = ar.right - cr.left - el.offsetWidth;
    // keep inside viewport
    const vw = innerWidth, vh = innerHeight;
    const absLeft = left + cr.left, absTop = top + cr.top;
    if (absLeft + el.offsetWidth > vw - 8) left -= (absLeft + el.offsetWidth) - (vw - 8);
    if (left + cr.left < 8) left = 8 - cr.left;
    if (absTop + el.offsetHeight > vh - 8) { // flip above
      top = ar.top - cr.top - gap - el.offsetHeight + (container === doc.body ? scrollY : 0);
      if (top + cr.top < 8) top = 8 - cr.top;
    }
    el.style.left = left + 'px'; el.style.top = top + 'px';
  }
  function buildMenu(items) {
    const m = doc.createElement('div'); m.className = 'mac-menu'; m.role = 'menu';
    const hasChecks = items.some(i => i && i.checked !== undefined);
    if (hasChecks) m.classList.add('has-checks');
    items.forEach(it => {
      if (it === '-' || it === null) { const s = doc.createElement('div'); s.className = 'mac-menu-separator'; m.appendChild(s); return; }
      if (typeof it === 'string') it = { label: it };
      if (it.section) { const s = doc.createElement('div'); s.className = 'mac-menu-section'; s.textContent = it.section; m.appendChild(s); return; }
      const b = doc.createElement('button'); b.className = 'mac-menu-item'; b.role = 'menuitem'; b.type = 'button';
      if (it.disabled) b.classList.add('disabled');
      if (it.destructive) b.classList.add('destructive');
      let html = '';
      if (hasChecks) html += `<span class="check">${it.checked ? '<i class="sf" data-sf="checkmark"></i>' : ''}</span>`;
      if (it.icon) html += `<i class="sf icon" data-sf="${it.icon}"></i>`;
      html += `<span class="label">${it.label}</span>`;
      if (it.shortcut) html += `<span class="shortcut">${it.shortcut}</span>`;
      if (it.submenu) html += `<span class="sub"><i class="sf" data-sf="chevron-right"></i></span>`;
      b.innerHTML = html; b.dataset.value = it.value != null ? it.value : it.label;
      on(b, 'click', () => { if (it.onSelect) it.onSelect(it); });
      m.appendChild(b);
    });
    return m;
  }
  const Menu = {
    open(anchor, menuOrItems, opts = {}) {
      closeAll();
      let menu = Array.isArray(menuOrItems) ? buildMenu(menuOrItems) : resolve(menuOrItems, anchor);
      if (!menu) return null;
      if (Array.isArray(menuOrItems)) { menu._temp = true; doc.body.appendChild(menu); }
      menu.hidden = false; menu.style.position = menu._temp ? 'absolute' : (getComputedStyle(menu).position === 'static' ? 'absolute' : getComputedStyle(menu).position);
      if (global.MacOSIcons) MacOSIcons.render(menu);
      menu._anchor = anchor; menu._onClose = opts.onClose; menu._keep = opts.keep;
      if (opts.at) { menu.style.left = opts.at.x + 'px'; menu.style.top = opts.at.y + 'px'; fitInViewport(menu); }
      else if (anchor) positionBelow(anchor, menu, opts);
      if (anchor) anchor.classList.add('is-open');
      Layers.open.add(menu);
      // selecting an item closes the menu
      const onItem = e => { const it = e.target.closest('.mac-menu-item'); if (!it || it.classList.contains('disabled')) return; if (it.querySelector('.sub')) return; emit(menu, 'mac:select', { value: it.dataset.value, item: it }); if (opts.onSelect) opts.onSelect(it.dataset.value, it); setTimeout(() => closeLayer(menu), 60); };
      menu.addEventListener('click', onItem, { once: false });
      menu._onClose = () => { menu.removeEventListener('click', onItem); if (opts.onClose) opts.onClose(); };
      return menu;
    },
    close: closeAll
  };
  function fitInViewport(el) {
    const r = el.getBoundingClientRect();
    if (r.right > innerWidth - 8) el.style.left = (parseFloat(el.style.left) - (r.right - innerWidth + 8)) + 'px';
    if (r.bottom > innerHeight - 8) el.style.top = (parseFloat(el.style.top) - (r.bottom - innerHeight + 8)) + 'px';
  }
  // data-menu="#id" on any element → click opens that menu below it
  on(doc, 'click', e => {
    const a = e.target.closest('[data-menu]');
    if (!a || e.target.closest('.mac-menu')) return;
    const menu = resolve(a.dataset.menu, a);
    if (!menu) return;
    if (!menu.hidden && Layers.open.has(menu)) { closeLayer(menu); return; }
    Menu.open(a, menu, { align: a.dataset.menuAlign, gap: a.dataset.menuGap != null ? +a.dataset.menuGap : (a.classList.contains('mac-menubar-item') ? 6 : 4) });
  });
  // menu bar: hovering another title while a menu is open switches menus
  on(doc, 'pointerover', e => {
    const a = e.target.closest('.mac-menubar-item[data-menu]');
    if (!a) return;
    const anyOpen = Array.from(Layers.open).find(l => l._anchor && l._anchor.classList.contains('mac-menubar-item'));
    if (anyOpen && anyOpen._anchor !== a) { const menu = resolve(a.dataset.menu, a); if (menu) Menu.open(a, menu, { gap: 6 }); }
  });
  // popup / pulldown buttons with data-options="A|B|C" or data-menu
  on(doc, 'click', e => {
    const p = e.target.closest('.mac-popup[data-options], .mac-pulldown[data-options]');
    if (!p) return;
    const labelEl = p.querySelector('.label');
    const current = labelEl ? labelEl.textContent.trim() : '';
    const items = p.dataset.options.split('|').map(o => {
      if (o === '-') return '-';
      const [label, value] = o.split('=');
      const item = { label, value: value != null ? value : label };
      if (p.classList.contains('mac-popup')) item.checked = label === current;
      return item;
    });
    Menu.open(p, items, {
      onSelect: (v, it) => {
        if (p.classList.contains('mac-popup') && labelEl) labelEl.textContent = it.querySelector('.label').textContent;
        emit(p, 'change', { value: v });
        if (p.dataset.prefSelect) Theme.set(p.dataset.prefSelect, v);
      }
    });
  });
  // context menus: data-contextmenu="#id" on a surface
  on(doc, 'contextmenu', e => {
    const s = e.target.closest('[data-contextmenu]');
    if (!s) return;
    const menu = resolve(s.dataset.contextmenu, s);
    if (!menu) return;
    e.preventDefault();
    const container = menu.offsetParent || doc.body;
    const cr = container === doc.body ? { left: -scrollX, top: -scrollY } : container.getBoundingClientRect();
    Menu.open(null, menu, { at: { x: e.clientX - cr.left, y: e.clientY - cr.top } });
  });

  /* ---------------- Popovers ---------------- */
  function placePopover(anchor, pop, placement) {
    pop.hidden = false; pop.dataset.placement = placement;
    const container = pop.offsetParent || doc.body;
    const cr = container === doc.body ? { left: -scrollX, top: -scrollY } : container.getBoundingClientRect();
    const ar = anchor.getBoundingClientRect(); const pw = pop.offsetWidth, ph = pop.offsetHeight; const gap = 12;
    let left, top;
    switch (placement) {
      case 'top': left = ar.left + ar.width / 2 - pw / 2; top = ar.top - ph - gap; break;
      case 'left': left = ar.left - pw - gap; top = ar.top + ar.height / 2 - ph / 2; break;
      case 'right': left = ar.right + gap; top = ar.top + ar.height / 2 - ph / 2; break;
      default: left = ar.left + ar.width / 2 - pw / 2; top = ar.bottom + gap;
    }
    left = clamp(left, 8, innerWidth - pw - 8); top = clamp(top, 8, innerHeight - ph - 8);
    pop.style.left = (left - cr.left) + 'px'; pop.style.top = (top - cr.top) + 'px';
    // arrow alignment on the anchor center
    const arrow = pop.querySelector('.mac-popover-arrow');
    if (arrow && (placement === 'bottom' || placement === 'top')) { const ax = ar.left + ar.width / 2 - left; arrow.style.left = clamp(ax - 7, 14, pw - 28) + 'px'; }
    if (arrow && (placement === 'left' || placement === 'right')) { const ay = ar.top + ar.height / 2 - top; arrow.style.top = clamp(ay - 7, 14, ph - 28) + 'px'; }
  }
  const Popover = {
    toggle(anchor, pop, placement = 'bottom') {
      pop = resolve(pop, anchor); if (!pop) return;
      if (!pop.hidden && Layers.open.has(pop)) { closeLayer(pop); return; }
      closeAll();
      placePopover(anchor, pop, placement);
      pop._anchor = anchor; anchor.classList.add('is-open'); Layers.open.add(pop);
    },
    close: closeAll
  };
  on(doc, 'click', e => {
    const a = e.target.closest('[data-popover]');
    if (!a || e.target.closest('.mac-popover')) return;
    Popover.toggle(a, a.dataset.popover, a.dataset.placement || 'bottom');
  });

  /* ---------------- Toggle panels (Control Center, Notification Center, …) ---------------- */
  on(doc, 'click', e => {
    const a = e.target.closest('[data-toggle]');
    if (!a) return;
    const el = resolve(a.dataset.toggle, a); if (!el) return;
    if (!el.hidden && Layers.open.has(el)) { closeLayer(el); return; }
    closeAll();
    el.hidden = false; el._anchor = a; el._keep = a.dataset.keep || null; a.classList.add('is-open'); Layers.open.add(el);
    if (global.MacOSIcons) MacOSIcons.render(el);
    bindSliders(el);
  });
  on(doc, 'click', e => {
    const a = e.target.closest('[data-show]'); if (!a) return;
    const el = resolve(a.dataset.show, a); if (el) { el.hidden = false; if (global.MacOSIcons) MacOSIcons.render(el); bindSliders(el); }
  });
  on(doc, 'click', e => {
    const a = e.target.closest('[data-hide]'); if (!a) return;
    const el = resolve(a.dataset.hide, a); if (el) el.hidden = true;
  });

  /* ---------------- Sheets ---------------- */
  on(doc, 'click', e => {
    const a = e.target.closest('[data-sheet]');
    if (a) { const s = resolve(a.dataset.sheet, a); if (s) { s.hidden = false; if (global.MacOSIcons) MacOSIcons.render(s); } }
    const c = e.target.closest('[data-sheet-close]');
    if (c) { const s = c.closest('.mac-scrim'); if (s) s.hidden = true; }
  });

  /* ---------------- Tooltips ---------------- */
  let tipEl = null, tipTimer = 0;
  on(doc, 'pointerover', e => {
    const a = e.target.closest('[data-tooltip]');
    if (!a || a._tipShown) return;
    clearTimeout(tipTimer);
    tipTimer = setTimeout(() => {
      hideTip();
      tipEl = doc.createElement('div'); tipEl.className = 'mac-tooltip' + (a.dataset.tooltipClass ? ' ' + a.dataset.tooltipClass : ''); tipEl.textContent = a.dataset.tooltip;
      doc.body.appendChild(tipEl);
      const r = a.getBoundingClientRect();
      tipEl.style.left = clamp(r.left + r.width / 2 - tipEl.offsetWidth / 2, 6, innerWidth - tipEl.offsetWidth - 6) + scrollX + 'px';
      tipEl.style.top = (r.top - tipEl.offsetHeight - 10 + scrollY) + 'px';
      a._tipShown = true;
      const leave = () => { a._tipShown = false; hideTip(); a.removeEventListener('pointerleave', leave); a.removeEventListener('pointerdown', leave); };
      on(a, 'pointerleave', leave); on(a, 'pointerdown', leave);
    }, a.dataset.tooltipDelay != null ? +a.dataset.tooltipDelay : 450);
  });
  on(doc, 'pointerout', e => { if (e.target.closest && e.target.closest('[data-tooltip]')) clearTimeout(tipTimer); });
  function hideTip() { if (tipEl) { tipEl.remove(); tipEl = null; } }

  /* ---------------- Windows: focus, drag, traffic lights ---------------- */
  let zTop = 10;
  const Windows = {
    focus(win) {
      if (!win) return;
      const scope = win.parentElement || doc;
      $$('.mac-window', scope).forEach(w => w.classList.toggle('inactive', w !== win));
      win.style.zIndex = String(++zTop);
      emit(win, 'mac:focus');
    },
    close(win) { win.hidden = true; emit(win, 'mac:close'); },
    minimize(win) {
      win.style.transition = 'transform .28s cubic-bezier(.4,0,1,1), opacity .28s';
      win.style.transformOrigin = '50% 140%'; win.style.transform = 'scale(.15) translateY(120%)'; win.style.opacity = '0';
      setTimeout(() => { win.hidden = true; win.style.transform = ''; win.style.opacity = ''; win.style.transition = ''; emit(win, 'mac:minimize'); }, 280);
    },
    zoom(win) {
      const z = win.classList.toggle('is-zoomed');
      if (z) { win._rect = { left: win.style.left, top: win.style.top, width: win.style.width, height: win.style.height }; Object.assign(win.style, { left: '8px', top: 'calc(var(--menubar-h) + 8px)', width: 'calc(100% - 16px)', height: 'calc(100% - var(--menubar-h) - 16px - 84px)' }); }
      else if (win._rect) Object.assign(win.style, win._rect);
    },
    show(win) { win.hidden = false; this.focus(win); }
  };
  on(doc, 'pointerdown', e => {
    const win = e.target.closest('.mac-window'); if (!win) return;
    if (win.classList.contains('floating') || win.dataset.focusable != null) Windows.focus(win);
    // drag by titlebar/toolbar (floating windows only)
    const bar = e.target.closest('.mac-titlebar, .mac-toolbar, [data-drag-handle]');
    if (!bar || !win.classList.contains('floating')) return;
    if (e.target.closest('button, input, select, textarea, a, .mac-search, .mac-segmented, [data-menu], [data-popover]')) return;
    if (win.classList.contains('is-zoomed')) return;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY, sl = win.offsetLeft, st = win.offsetTop;
    const parent = win.offsetParent || doc.body; const pw = parent.clientWidth, ph = parent.clientHeight;
    win.classList.add('is-dragging');
    const move = ev => { win.style.left = clamp(sl + ev.clientX - startX, -win.offsetWidth + 80, pw - 80) + 'px'; win.style.top = clamp(st + ev.clientY - startY, 0, ph - 40) + 'px'; };
    const up = () => { win.classList.remove('is-dragging'); doc.removeEventListener('pointermove', move); doc.removeEventListener('pointerup', up); };
    on(doc, 'pointermove', move); on(doc, 'pointerup', up);
  });
  on(doc, 'click', e => {
    const tl = e.target.closest('.mac-traffic-lights > span'); if (!tl) return;
    const win = tl.closest('.mac-window'); if (!win || win.dataset.static != null) return;
    if (tl.classList.contains('close')) Windows.close(win);
    else if (tl.classList.contains('minimize')) Windows.minimize(win);
    else if (tl.classList.contains('zoom')) Windows.zoom(win);
  });
  on(doc, 'dblclick', e => { const bar = e.target.closest('.mac-window.floating > .mac-toolbar, .mac-window.floating > .mac-titlebar'); if (bar && !e.target.closest('button,input')) Windows.zoom(bar.closest('.mac-window')); });
  // data-window="#id" → show/focus a window (dock items, menu items)
  on(doc, 'click', e => {
    const a = e.target.closest('[data-window]'); if (!a) return;
    const win = resolve(a.dataset.window, a); if (!win) return;
    if (win.hidden) Windows.show(win); else if (a.classList.contains('mac-dock-item') && !win.classList.contains('inactive')) Windows.minimize(win); else Windows.focus(win);
    if (a.classList.contains('mac-dock-item')) a.classList.add('running');
  });

  /* ---------------- Dock magnification ---------------- */
  function bindDock(dock) {
    if (!dock || dock._mac) return; dock._mac = 1;
    const items = () => $$('.mac-dock-item', dock);
    const mag = dock.dataset.magnify === 'false' ? 0 : (+dock.dataset.magnify || 0.55);
    on(dock, 'pointermove', e => {
      if (!mag) return;
      items().forEach(it => {
        const r = it.getBoundingClientRect(); const cx = r.left + r.width / 2; const d = Math.abs(e.clientX - cx);
        const s = 1 + mag * Math.max(0, 1 - d / 120);
        it.style.setProperty('--scale', s.toFixed(3));
      });
    });
    on(dock, 'pointerleave', () => items().forEach(it => it.style.setProperty('--scale', 1)));
    on(dock, 'click', e => {
      const it = e.target.closest('.mac-dock-item'); if (!it) return;
      if (!it.classList.contains('running') && !it.dataset.window) { it.classList.add('bounce'); setTimeout(() => { it.classList.remove('bounce'); it.classList.add('running'); }, 1400); }
      else if (it.dataset.window) { it.classList.add('bounce'); setTimeout(() => it.classList.remove('bounce'), 700); }
    });
    // build tooltips from data-label
    items().forEach(it => { if (it.dataset.label && !it.querySelector('.mac-tooltip')) { const t = doc.createElement('div'); t.className = 'mac-tooltip dock'; t.textContent = it.dataset.label; it.appendChild(t); } });
  }

  /* ---------------- Spotlight ---------------- */
  const Spotlight = {
    el: null,
    open() { this.el = this.el || $('.mac-spotlight[data-spotlight]'); if (!this.el) return; closeAll(); this.el.hidden = false; const i = $('input', this.el); if (i) { i.value = ''; i.focus(); } this.filter(''); emit(doc, 'mac:spotlight', { open: true }); },
    close() { if (this.el && !this.el.hidden) { this.el.hidden = true; emit(doc, 'mac:spotlight', { open: false }); } },
    toggle() { this.el = this.el || $('.mac-spotlight[data-spotlight]'); if (!this.el) return; this.el.hidden ? this.open() : this.close(); },
    filter(q) {
      if (!this.el) return; q = q.trim().toLowerCase();
      const rows = $$('[data-search]', this.el); let any = false;
      rows.forEach(r => { const hit = !q || r.dataset.search.toLowerCase().includes(q); r.hidden = !hit; if (hit) any = true; });
      $$('[data-spotlight-section]', this.el).forEach(s => { s.hidden = !$$('[data-search]:not([hidden])', s).length; });
      const empty = $('[data-spotlight-empty]', this.el); if (empty) empty.hidden = any || !q;
      const panel = $('.mac-spotlight-panel', this.el); if (panel) panel.hidden = !q && panel.dataset.showEmpty !== 'true';
      // highlight first visible
      let first = true; rows.forEach(r => { r.classList.toggle('is-selected', !r.hidden && first && !!q); if (!r.hidden) first = false; });
    }
  };
  on(doc, 'keydown', e => {
    if ((e.metaKey || e.ctrlKey) && e.code === 'Space') { e.preventDefault(); Spotlight.toggle(); }
  });
  on(doc, 'input', e => { if (e.target.matches('.mac-spotlight[data-spotlight] input')) Spotlight.filter(e.target.value); });
  on(doc, 'pointerdown', e => { if (Spotlight.el && !Spotlight.el.hidden && !Spotlight.el.contains(e.target) && !e.target.closest('[data-spotlight-toggle]')) Spotlight.close(); }, true);
  on(doc, 'click', e => { if (e.target.closest('[data-spotlight-toggle]')) Spotlight.toggle(); });

  /* ---------------- Notifications ---------------- */
  const Notify = {
    stack() { let s = $('.mac-notif-stack[data-live]'); if (!s) { s = doc.createElement('div'); s.className = 'mac-notif-stack'; s.dataset.live = ''; doc.body.appendChild(s); } return s; },
    show({ app = 'App', icon = 'bell', iconClass = 'ic-blue', title = '', body = '', time = 'now', actions = [], duration = 6000, dark = false } = {}) {
      const n = doc.createElement('div'); n.className = 'mac-notification' + (dark ? ' dark' : '');
      n.innerHTML = `<button class="mac-close-btn" aria-label="Close"><i class="sf" data-sf="xmark"></i></button>
        <div class="mac-app-icon ${iconClass}"><i class="sf" data-sf="${icon}"></i></div>
        <div class="body"><div class="top"><b>${title || app}</b><span>${time}</span></div><div class="msg">${body}</div>
        ${actions.length ? `<div class="actions">${actions.map(a => `<button class="mac-btn">${a}</button>`).join('')}</div>` : ''}</div>`;
      if (global.MacOSIcons) MacOSIcons.render(n);
      const s = this.stack(); s.prepend(n);
      const remove = () => { n.style.transition = 'opacity .2s, transform .25s'; n.style.opacity = '0'; n.style.transform = 'translateX(40px)'; setTimeout(() => n.remove(), 250); };
      on($('.mac-close-btn', n), 'click', remove);
      if (duration) setTimeout(remove, duration);
      return n;
    }
  };

  /* ---------------- Live clock ---------------- */
  function tickClock() {
    $$('[data-clock]').forEach(el => {
      const d = new Date(); const fmt = el.dataset.clock || 'full';
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      let h = d.getHours(), m = String(d.getMinutes()).padStart(2, '0'); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12;
      el.textContent = fmt === 'time' ? `${h}:${m} ${ap}` : `${days[d.getDay()]} ${months[d.getMonth()]} ${d.getDate()} ${h}:${m} ${ap}`;
    });
  }

  /* ---------------- Code viewer helper (docs) ---------------- */
  function codeOf(el) {
    const clone = el.cloneNode(true);
    $$('[data-sf]', clone).forEach(i => { i.innerHTML = ''; delete i.dataset.rendered; });
    $$('.mac-tooltip.dock', clone).forEach(t => t.remove());
    let html = clone.outerHTML.replace(/ style="[^"]*"/g, '').replace(/ data-rendered="[^"]*"/g, '');
    // light pretty-print
    html = html.replace(/></g, '>\n<');
    const lines = html.split('\n'); let depth = 0; const out = [];
    for (let l of lines) {
      l = l.trim(); if (!l) continue;
      const closing = /^<\//.test(l), selfClosing = /\/>$/.test(l) || /^<(i|br|img|input|hr)\b[^>]*>$/.test(l) && !/<\/i>/.test(l);
      const opensAndCloses = /^<[^\/][^>]*>[^<]*<\/[^>]+>$/.test(l);
      if (closing) depth = Math.max(0, depth - 1);
      out.push('  '.repeat(depth) + l);
      if (!closing && !selfClosing && !opensAndCloses && /^<[^\/!]/.test(l) && !/<\/[^>]+>$/.test(l)) depth++;
    }
    return out.join('\n');
  }
  const esc = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  /* ---------------- Init ---------------- */
  function init(root) {
    Theme.load();
    if (global.MacOSIcons) MacOSIcons.render(root || doc);
    bindSliders(root);
    $$('.mac-dock', root || doc).forEach(bindDock);
    $$('.mac-popover, .mac-menu:not(.static), .mac-cc:not(.static), .mac-nc:not(.static)', root || doc).forEach(el => { if (!el.hasAttribute('data-open')) el.hidden = true; });
    $$('.mac-spotlight[data-spotlight]').forEach(s => { s.hidden = true; });
    tickClock(); setInterval(tickClock, 1000);
    // popup labels from data-pref-label handled by syncThemeUI
    syncThemeUI();
    emit(doc, 'mac:ready');
  }
  if (doc.readyState === 'loading') on(doc, 'DOMContentLoaded', () => init()); else init();

  global.MacOS = { Theme, Menu, Popover, Windows, Spotlight, Notify, icons: global.MacOSIcons, bindDock, bindSliders, closeAll, codeOf, esc, init, $, $$ };
})(window);
